import { capTypesForRTM, getVitalsLabelsFromEnrollment } from '../../utils/enrollmentFields';

// Enrollments arrive keyed by UUID, and a JSON object's key order is the
// backend's map order, not a meaningful one. The backend therefore sends an
// explicit displayOrder (newest enrollment first) and that is the only thing
// worth sorting on here.
export function sortEnrollmentsForDisplay(enrollments) {
  return Object.entries(enrollments || {})
    .map(([enrollmentUUID, enrollment]) => ({ ...enrollment, enrollmentUUID }))
    .sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0));
}

// Returns the value every selected enrollment carries, or undefined when any of
// them disagrees or leaves it unset. An enrollment without a value disagrees
// just as loudly as one with a different value: applying the only programme that
// has a provider order number to an order covering two would misattribute it.
// An empty field the shipper has to fill is a prompt; a value inherited from
// whichever enrollment happened to sort first is a silent wrong answer.
function sharedValue(values) {
  if (!values.length) return undefined;
  const [first] = values;
  if (first === undefined || first === null || first === '') return undefined;
  return values.every(value => value === first) ? first : undefined;
}

// Route state for creating a direct order from an awaiting-kit patient row.
// target_org_uuid is what lets OrderDetails load the patient org's catalog;
// billing stays unset so invoicing keeps its oldest-ancestor resolution.
export function buildDirectOrderRouteState(patient, selectedPrograms) {
  const enrollments = selectedPrograms?.length
    ? selectedPrograms.map(id => patient.patientEnrollment.enrollments[id])
    : sortEnrollmentsForDisplay(patient.patientEnrollment?.enrollments);
  const vitals = [...new Set(enrollments.flatMap(enrollment => getVitalsLabelsFromEnrollment(enrollment) || []))];
  const deviceModel = sharedValue(enrollments.map(enrollment => enrollment.rtmDeviceModel));

  return {
    order: {
      patient_uuid: patient.masked_id,
      target_org_uuid: patient.orgUUID,
      enrollment_uuids: enrollments.map(enrollment => enrollment.enrollmentUUID),
      order_category: 'direct',
      contact_details: {
        recipient_name: patient.patientName,
        phone: patient.textPhoneNo,
        email: patient.email,
      },
      po_number: sharedValue(enrollments.map(enrollment => enrollment.providerOrderNo)),
      shipping_address: patient.address,
      vitals: vitals.length ? vitals : undefined,
      deviceType: capTypesForRTM.find(c => c.value === deviceModel)?.label,
    },
  };
}
